import { useState, useRef, useEffect } from "react";
import { animated, useTransition } from "@react-spring/web";
import useMeasure from "react-use-measure";
import classNames from "classnames";
import LanguageSelectItem from "components/atoms/LanguageSelectItem";
import { LanguageType } from "constants/languages";
import LanguageSelectFormContainer from "containers/molecules/LanguageSelectFormContainer";
import useTranslate from "hooks/useTranslate";
import useClickOutside from "hooks/useClickOutside";
import GlobusIcon from "assets/icons/globus.svg?react";
import "./styles.sass";

type LanguageSelectProps = {
  language: LanguageType;
  // eslint-disable-next-line react/no-unused-prop-types
  showArrows?: boolean;
  isShort?: boolean;
  onClick?: () => void;
};

const LanguageSelect = (props: LanguageSelectProps) => {
  const { language, onClick, isShort } = props;
  const listRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslate();

  useEffect(() => {
    setIsExpanded(false);
  }, [language]);

  const onClose = () => {
    setIsExpanded(false);
  };

  const listClass = classNames("LanguageSelect__list", {
    active: isExpanded,
  });

  useClickOutside({ refs: [listRef], handler: onClose });

  const [ref] = useMeasure();

  const transitions = useTransition(isExpanded, {
    from: { opacity: 0, transform: "scale(0.9)" },
    enter: { opacity: 1, transform: "scale(1)" },
    leave: { opacity: 0, transform: "scale(0.9)" },
    reverse: isExpanded,
    config: { tension: 200, friction: 20 },
  });
  return (
    // eslint-disable-next-line react/jsx-handler-names
    <div className="LanguageSelect" ref={listRef} onClick={onClick}>
      {isShort ? (
        <div className="LanguageSelect__short">
          <GlobusIcon className="LanguageSelect__shotIcon" />{" "}
          <span>{language.isoCode}</span>
        </div>
      ) : (
        <LanguageSelectItem
          language={language}
          showArrows={false}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      )}

      {transitions(
        (style, item) =>
          item && (
            <animated.div className={listClass} style={{ ...style }}>
              <div ref={ref} className="LanguageSelect__list_content">
                <span className="LanguageSelect__list_content_header">
                  {t("language.select")}
                </span>
                <LanguageSelectFormContainer />
              </div>
            </animated.div>
          ),
      )}
    </div>
  );
};

export default LanguageSelect;
